import { useCallback, useSyncExternalStore } from 'react'
import { INITIAL_WIZARD_DATA, type WizardData } from '../types'

const LS_KEY = 'fundsorter_onboarding_wizard'

// ── Subscription ────────────────────────────────────────────
// Components subscribe via useSyncExternalStore. Writes dispatch
// a custom event so all subscribers re-render with the new snapshot.

const EVENT_NAME = 'wizarddata-change'

function subscribe(callback: () => void): () => void {
  window.addEventListener(EVENT_NAME, callback)
  return () => window.removeEventListener(EVENT_NAME, callback)
}

function notify(): void {
  window.dispatchEvent(new Event(EVENT_NAME))
}

// ── Snapshot ────────────────────────────────────────────────
// Cache the parsed result and only re-parse when the raw string changes.

let cachedRaw: string | null = null
let cachedData: WizardData = INITIAL_WIZARD_DATA

function getSnapshot(): WizardData {
  const raw = localStorage.getItem(LS_KEY)
  if (raw !== cachedRaw) {
    cachedRaw = raw
    try {
      cachedData = raw ? { ...INITIAL_WIZARD_DATA, ...JSON.parse(raw) } : INITIAL_WIZARD_DATA
    } catch {
      cachedData = INITIAL_WIZARD_DATA
    }
  }
  return cachedData
}

// ── Write helpers (used by hook and by external callers) ─────

export function writeWizardData(data: WizardData): void {
  try {
    const json = JSON.stringify(data)
    localStorage.setItem(LS_KEY, json)
    cachedRaw = json
    cachedData = data
  } catch {
    // Quota exceeded — fail silently
  }
  notify()
}

export function readWizardData(): WizardData {
  return getSnapshot()
}

export function clearWizardData(): void {
  localStorage.removeItem(LS_KEY)
  cachedRaw = null
  cachedData = INITIAL_WIZARD_DATA
  notify()
}

// ── Hook ────────────────────────────────────────────────────

type WizardUpdater = Partial<WizardData> | ((prev: WizardData) => Partial<WizardData>)

export function useWizardData(): [WizardData, (updater: WizardUpdater) => void] {
  const data = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  const setData = useCallback((updater: WizardUpdater) => {
    const current = getSnapshot()
    const partial = typeof updater === 'function' ? updater(current) : updater
    writeWizardData({ ...current, ...partial })
  }, [])

  return [data, setData]
}
